// StreamChatContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { StreamChat } from "stream-chat";
import { auth, db } from "../api/firebase";
import { doc, getDoc } from "firebase/firestore";
export const StreamChatContext = createContext(null);

// 切断までの猶予期間（ミリ秒）。この間にチャット画面に戻ってきたら切断をキャンセルして再利用する。
const DISCONNECT_GRACE_MS = 500;

// モジュールスコープで保持: 複数の Provider インスタンス（unmount→mount）をまたいで調整する
let pendingDisconnectTimer = null;

export const StreamChatProvider = ({ children }) => {
  const [chatClient, setChatClient] = useState(null);
  const chatClientRef = useRef(null);

  useEffect(() => {
    // 直前のunmountで予約された切断があればキャンセル（素早く戻ってきた場合の再利用）
    if (pendingDisconnectTimer) {
      clearTimeout(pendingDisconnectTimer);
      pendingDisconnectTimer = null;
    }

    const connectChatClient = async (user) => {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      let userData;
      if (!userDocSnap.exists()) {
        console.error("User document does not exist in Firestore");
        userData = {
          uid: user?.uid,
          name: user?.displayName,
          avatar: user?.photoURL,
        };
      } else {
        userData = userDocSnap.data();
      }

      const token = await fetch(process.env.REACT_APP_STREAM_GET_TOKEN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          input: userData.uid,
        }),
      }).then((r) => r.json());

      if (
        !chatClientRef.current ||
        chatClientRef.current.userID !== userData.uid
      ) {
        const client = StreamChat.getInstance(
          process.env.REACT_APP_STREAM_API_KEY
        );
        await client.connectUser(
          {
            id: userData.uid,
            name: userData.name,
            image: userData.avatar,
          },
          token
        );
        chatClientRef.current = client;
        setChatClient(client);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        connectChatClient(user);
      } else if (chatClientRef.current) {
        chatClientRef.current.disconnectUser().catch((err) => {
          console.warn("[GetStream] disconnect error (logout):", err);
        });
        chatClientRef.current = null;
        setChatClient(null);
      }
    });

    return () => {
      unsubscribe();
      const client = chatClientRef.current;
      chatClientRef.current = null;
      if (client) {
        // 猶予期間を設けて切断。子コンポーネントの非同期cleanup（channel.stopWatching等）の完了を待つ。
        pendingDisconnectTimer = setTimeout(() => {
          client.disconnectUser().catch((err) => {
            console.warn("[GetStream] disconnect error (unmount):", err);
          });
          pendingDisconnectTimer = null;
        }, DISCONNECT_GRACE_MS);
      }
    };
  }, []);

  return (
    <StreamChatContext.Provider value={chatClient}>
      {children ?? <Outlet />}
    </StreamChatContext.Provider>
  );
};

export const useStreamChatClient = () => useContext(StreamChatContext);
